'use client';

import { useState } from 'react';
import { ClipboardCopy, FileText, MessageSquare, PenLine, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface QueryMessageProps {
  query: string;
  resumeTitle?: string | null;
  onEdit?: (query: string) => void;
  onDelete?: () => void;
  className?: string;
}

export function QueryMessage({
  query,
  resumeTitle,
  onEdit,
  onDelete,
  className,
}: QueryMessageProps) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    void navigator.clipboard.writeText(query);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const truncatedTitle = resumeTitle && resumeTitle.length > 30
    ? resumeTitle.slice(0, 30) + '…'
    : resumeTitle;

  return (
    <div className={cn('group/qm relative px-3 pt-2.5 sm:px-4', className)}>
      <div className="flex items-start gap-2.5">
        <MessageSquare className="mt-0.5 h-3 w-3 shrink-0 text-primary/70" />
        <div className="min-w-0 flex-1 pb-2.5">
          <p className="pt-0.5 text-xs font-medium text-foreground/85">
            {query}
          </p>
          {resumeTitle && (
            <div className="mt-1 flex items-center gap-1">
              <FileText className="h-3 w-3 shrink-0 text-primary/60" />
              <span className="text-[11px] text-muted-foreground">
                Attached: {truncatedTitle}
              </span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(query)}
              className="inline-flex items-center rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Edit query"
            >
              <PenLine className="h-3 w-3" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Delete query"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
